// Grids & Gantts — the grid's constants, kept out of the grid.
//
// THIS FILE EXISTS FOR ONE REASON: React Fast Refresh. A .jsx module that
// exports anything other than components cannot be hot-updated — Vite
// invalidates it and FULL-RELOADS the page instead:
//
//   hmr invalidate Grid.jsx Could not Fast Refresh
//   ("FOLDER_ICONS" export is incompatible)
//
// On most pages that costs a second. On this one it costs a three-minute
// rebuild of the whole structure, every time anybody touches the grid — so
// the row height and the folder glyphs live here, and Grid.jsx exports its
// component and nothing else.

// Row height. 28 was as tight as the numbers would go and tighter than
// anybody wants to read for an hour — a structure is scanned down a column,
// and rows that touch make the eye lose its place. 34 with real side
// padding is the same information, legibly.
export const ROW_H = 34;

// ON A TOUCH SCREEN A ROW IS A TARGET, not just a line of text. 34px is
// comfortable to read with a mouse and small to hit with a thumb — the usual
// guidance is about 44 — and on this page a row carries several targets side
// by side: a twisty, a name, a status chip, an editable cell. Taller rows
// mean fewer on screen, which is the trade, and on a phone it is worth it
// because the alternative is tapping the wrong one.
export const ROW_H_TOUCH = 46;

// The folder glyphs the structure uses. A NAME in the config, not a code
// point, so a fork can repoint them without a config full of emoji.
export const FOLDER_ICONS = {
  folder: '📁', check: '✅', gear: '⚙️', trophy: '🏆', flag: '🚩', star: '⭐',
  clock: '🕒', box: '📦', chart: '📊', people: '👥', money: '💰', wrench: '🔧',
};

// A FOLDER'S ICON is one of the names above, ANY emoji (or a few characters
// of text) typed in its place, or the address of a small picture. What it
// resolves to: { text } or { src }. An empty or unreadable one is 📁.
export const isIconUrl = (v) => /^(https?:\/\/|\/|data:image\/)/i.test(String(v || '').trim());
export function iconSpec(name) {
  const v = String(name == null ? '' : name).trim();
  if (FOLDER_ICONS[v]) return { text: FOLDER_ICONS[v] };
  if (isIconUrl(v)) return { src: v };
  // Not a name: a plain lower-case word is a name this build does not know
  // (an older or newer config) — that is the folder, never the word itself.
  if (v && !/^[a-z]+$/.test(v)) return { text: [...v].slice(0, 4).join('') };
  return { text: FOLDER_ICONS.folder };
}
export const iconOf = (name) => iconSpec(name).text || FOLDER_ICONS.folder;

// ---- status categories -------------------------------------------------------
// A status name on its own does not say where the work IS. "RFQ - QUEUE"
// could be anything; its CATEGORY says whether it is not started, under way
// or finished, and that is what a reader is actually scanning for.
//
// The palette is Folders' own (Structure.jsx), on purpose — the same status
// must not be grey here and blue there:
//   blue-gray  → not started   (grey)
//   yellow     → in progress   (blue — Jira's "yellow" category, painted
//                               blue in this product; matching Folders
//                               matters more than matching Jira's name)
//   green      → done          (green)
export const STATUS_FILL = {
  'blue-gray': '#6b7280',
  yellow: '#4a90d9',
  green: '#16a34a',
  'medium-gray': '#6b7280',
  undefined: '#6b7280',
};

export const categoryColorOf = (statusLike) => {
  const cat = statusLike && (statusLike.statusCategory || statusLike);
  const named = cat && (cat.colorName || cat.color);
  if (named) return String(named);
  const key = String((cat && cat.key) || '').toLowerCase();
  if (key === 'done') return 'green';
  if (key === 'indeterminate') return 'yellow';
  if (key === 'new') return 'blue-gray';
  return 'blue-gray';
};

export const statusFill = (statusLike) => STATUS_FILL[categoryColorOf(statusLike)] || STATUS_FILL['blue-gray'];

export const CATEGORY_WORD = { 'blue-gray': 'To do', yellow: 'In progress', green: 'Done' };
export const categoryWord = (statusLike) => CATEGORY_WORD[categoryColorOf(statusLike)] || 'To do';
